// Sprint 2026-Jun — VoltLedger integration Phase C
// Aging Outstanding Report (Receivables)
//
// Path: /reports/aging-outstanding
// Endpoints:
//   POST /api/reports/aging-outstanding/summary  → bucket cards + group pivot + reconciliation
//   POST /api/reports/aging-outstanding/drill    → paginated invoice rows for a group/bucket
//   POST /api/reports/aging-outstanding/export.{xlsx|pdf}
//
// Money is as-on-date accurate (receipts / CN / DN up to the date only).
// Buckets are configurable in Company Setup → Receivables Settings.
// Any ledger mismatch surfaces as an amber reconciliation warning banner.

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { Card } from "@/components/ui/card";
import {
  AlertTriangle, ChevronLeft, ChevronRight, FileSpreadsheet, FileText,
  Filter, RotateCw, X,
} from "lucide-react";

const inr = (v) =>
  (Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });

const CARD_TONES = [
  "border-emerald-200 bg-emerald-50 text-emerald-900",
  "border-sky-200 bg-sky-50 text-sky-900",
  "border-amber-200 bg-amber-50 text-amber-900",
  "border-orange-200 bg-orange-50 text-orange-900",
  "border-rose-200 bg-rose-50 text-rose-900",
  "border-red-300 bg-red-100 text-red-900",
  "border-slate-300 bg-slate-100 text-slate-900",
];

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { as_of: today(), branch_id: "", client_id: "", project_id: "", project_type: "", ses_pending: false, group_by: "client" };

const selCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";

export default function AgingOutstandingReport() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [masters, setMasters] = useState({ branches: [], clients: [], projects: [], workTypes: [] });
  const [drill, setDrill] = useState(null); // {group_id, group_name, bucket, bucketLabel}

  useEffect(() => {
    (async () => {
      try {
        const [b, c, p] = await Promise.all([
          api.get("/branches"), api.get("/clients"), api.get("/projects-picker"),
        ]);
        const arr = (r) => (Array.isArray(r.data) ? r.data : r.data?.items || []);
        setMasters((m) => ({ ...m, branches: arr(b), clients: arr(c), projects: arr(p) }));
      } catch { /* masters are optional for the report itself */ }
    })();
    // Project Type master — tolerate permission failure independently.
    api.get("/work-types")
      .then((r) => setMasters((m) => ({ ...m, workTypes: (r.data || []).map((w) => w.name).filter(Boolean) })))
      .catch(() => {});
    run(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const body = (f) => ({
    as_of: f.as_of || null,
    branch_id: f.branch_id || null,
    client_id: f.client_id || null,
    project_id: f.project_id || null,
    project_type: f.project_type || null,
    ses_pending: !!f.ses_pending,
    group_by: f.group_by || "client",
  });

  async function run(f = filters) {
    setLoading(true);
    try {
      const res = await api.post("/reports/aging-outstanding/summary", body(f));
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load the aging report");
    } finally { setLoading(false); }
  }

  async function doExport(kind) {
    setExporting(kind);
    try {
      const res = await api.post(`/reports/aging-outstanding/export.${kind}`,
        body(filters), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aging_outstanding_${filters.as_of || today()}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind.toUpperCase()} export failed`);
    } finally { setExporting(""); }
  }

  const buckets = data?.buckets || [];
  const groups = data?.groups || [];
  const totals = data?.grand_totals || {};
  const recon = data?.reconciliation;
  const groupLabel = { client: "Client", project: "Project", branch: "Branch" }[filters.group_by] || "Client";

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="aging-outstanding-page">
      <PageHeader
        eyebrow="Documents & Reports"
        title="Aging Outstanding (Receivables)"
        subtitle="Client receivables aged by due date — SI due date → override → SES + N days → client credit days. Money is as-on-date accurate and reconciled against the debtor ledger."
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="btn-export-xlsx" disabled={!!exporting || loading} onClick={() => doExport("xlsx")}
              className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
              <FileSpreadsheet size={14} /> {exporting === "xlsx" ? "Exporting…" : "Excel"}
            </button>
            <button data-testid="btn-export-pdf" disabled={!!exporting || loading} onClick={() => doExport("pdf")}
              className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
              <FileText size={14} /> {exporting === "pdf" ? "Exporting…" : "PDF"}
            </button>
          </div>
        }
      />

      {/* Filters */}
      <Card className="mb-4">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2">
            <Filter size={12} /> Filters
          </h3>
          <button onClick={() => setShowFilters((v) => !v)} className="text-[10px] uppercase tracking-wider font-bold text-slate-400 hover:text-slate-600">
            {showFilters ? "Hide" : "Show"}
          </button>
        </div>
        {showFilters && (
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">As on Date</div>
              <input data-testid="aging-as-of" type="date" value={filters.as_of}
                onChange={(e) => set("as_of", e.target.value)} className={selCls} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Group By</div>
              <select data-testid="aging-group-by" value={filters.group_by}
                onChange={(e) => set("group_by", e.target.value)} className={selCls}>
                <option value="client">Client</option>
                <option value="project">Project</option>
                <option value="branch">Branch</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Branch</div>
              <select data-testid="aging-branch" value={filters.branch_id}
                onChange={(e) => set("branch_id", e.target.value)} className={selCls}>
                <option value="">All Branches</option>
                {masters.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Client</div>
              <select data-testid="aging-client" value={filters.client_id}
                onChange={(e) => set("client_id", e.target.value)} className={selCls}>
                <option value="">All Clients</option>
                {masters.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Project</div>
              <select data-testid="aging-project" value={filters.project_id}
                onChange={(e) => set("project_id", e.target.value)} className={selCls}>
                <option value="">All Projects</option>
                {masters.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Project Type</div>
              <select data-testid="aging-project-type" value={filters.project_type}
                onChange={(e) => set("project_type", e.target.value)} className={selCls}>
                <option value="">All Types</option>
                {masters.workTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700 font-semibold pb-2.5 cursor-pointer">
              <input type="checkbox" data-testid="aging-ses-pending" checked={!!filters.ses_pending}
                onChange={(e) => set("ses_pending", e.target.checked)} />
              SES Pending only
            </label>
            <div className="flex gap-2">
              <button data-testid="aging-apply" onClick={() => run()}
                className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5">
                <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
              </button>
              <button onClick={() => { setFilters(EMPTY); run(EMPTY); }}
                className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">
                Reset
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Reconciliation warnings — never silent */}
      {recon && !recon.ok && (
        <div data-testid="aging-recon-warning"
          className="mb-4 border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
          <div className="font-bold uppercase tracking-wider flex items-center gap-2 mb-1">
            <AlertTriangle size={14} /> Reconciliation Warnings ({recon.warnings.length})
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            {recon.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
          <a href="/settings/data-tools" data-testid="aging-recon-detail-link"
            className="inline-block mt-2 font-bold text-orange-700 underline hover:text-orange-800">
            Open Reconciliation Detail (Data Tools) →
          </a>
        </div>
      )}
      {recon && recon.ok && (
        <div data-testid="aging-recon-ok" className="mb-4 border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs text-emerald-800 font-medium">
          Reconciled — aging total ₹{inr(recon.aging_total)} ties out with the Sales Invoice outstanding and debtor-ledger postings as on {recon.as_of}.
        </div>
      )}

      {/* Bucket cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
        <div className={`border px-3 py-2.5 ${CARD_TONES[6]}`} data-testid="aging-card-total">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">Total Outstanding</div>
          <div className="text-sm font-bold mt-0.5">₹{inr(totals.outstanding)}</div>
          <div className="text-[10px] opacity-70">{totals.invoice_count || 0} invoices</div>
        </div>
        {buckets.map((b, i) => (
          <div key={b.key} className={`border px-3 py-2.5 ${CARD_TONES[i % 6]}`} data-testid={`aging-card-${b.key}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{b.label}</div>
            <div className="text-sm font-bold mt-0.5">₹{inr(b.amount)}</div>
            <div className="text-[10px] opacity-70">{b.count} invoices</div>
          </div>
        ))}
      </div>

      {/* Group pivot table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="aging-group-table">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{groupLabel}</th>
                {buckets.map((b) => (
                  <th key={b.key} className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider whitespace-nowrap">{b.label}</th>
                ))}
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider whitespace-nowrap">Outstanding</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider whitespace-nowrap">Receipts</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider whitespace-nowrap">CN</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider whitespace-nowrap">DN</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider whitespace-nowrap">Ret. Pending</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider whitespace-nowrap" title="Total Outstanding − Pending Retention">Net Collectible</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={buckets.length + 7} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
              )}
              {!loading && groups.length === 0 && (
                <tr><td colSpan={buckets.length + 7} className="px-3 py-8 text-center text-slate-400">No outstanding receivables for the selected filters.</td></tr>
              )}
              {!loading && groups.map((g) => (
                <tr key={g.group_id} className="border-b border-slate-100 hover:bg-orange-50/40">
                  <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">
                    <button className="hover:text-orange-600 hover:underline text-left"
                      data-testid={`aging-group-${g.group_id}`}
                      onClick={() => setDrill({ group_id: g.group_id, group_name: g.group_name, bucket: "", bucketLabel: "All buckets" })}>
                      {g.group_name}
                    </button>
                    <span className="ml-1.5 text-[10px] text-slate-400">({g.invoice_count})</span>
                  </td>
                  {buckets.map((b) => (
                    <td key={b.key} className="px-3 py-2 text-right tabular-nums">
                      {g[b.key] > 0 ? (
                        <button className="hover:text-orange-600 hover:underline"
                          onClick={() => setDrill({ group_id: g.group_id, group_name: g.group_name, bucket: b.key, bucketLabel: b.label })}>
                          {inr(g[b.key])}
                        </button>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-bold tabular-nums">{inr(g.outstanding)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{inr(g.total_paid)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{inr(g.credit_note_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{inr(g.debit_note_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{inr(g.pending_retention)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sky-700 font-semibold">{inr(g.net_collectible)}</td>
                </tr>
              ))}
            </tbody>
            {!loading && groups.length > 0 && (
              <tfoot>
                <tr className="bg-orange-50 font-bold border-t-2 border-orange-200" data-testid="aging-grand-totals">
                  <td className="px-3 py-2.5 uppercase text-[10px] tracking-wider">Total ({groups.length})</td>
                  {buckets.map((b) => (
                    <td key={b.key} className="px-3 py-2.5 text-right tabular-nums">{inr(totals[b.key])}</td>
                  ))}
                  <td className="px-3 py-2.5 text-right tabular-nums">{inr(totals.outstanding)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{inr(totals.total_paid)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{inr(totals.credit_note_total)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{inr(totals.debit_note_total)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{inr(totals.pending_retention)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-sky-800" data-testid="aging-total-net-collectible">{inr(totals.net_collectible)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {drill && (
        <DrillModal drill={drill} filters={filters} onClose={() => setDrill(null)} />
      )}
    </div>
  );
}

function DrillModal({ drill, filters, onClose }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 25;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.post("/reports/aging-outstanding/drill", {
          as_of: filters.as_of || null,
          branch_id: filters.branch_id || null,
          client_id: filters.client_id || null,
          project_id: filters.project_id || null,
          project_type: filters.project_type || null,
          ses_pending: !!filters.ses_pending,
          group_by: filters.group_by || "client",
          group_id: drill.group_id,
          bucket: drill.bucket || null,
          skip: page * LIMIT,
          limit: LIMIT,
        });
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load invoices");
      } finally { setLoading(false); }
    })();
  }, [drill, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="aging-drill-modal" onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-6xl max-h-[85vh] flex flex-col shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-600">Invoice Drill-down</div>
            <div className="text-sm font-bold text-slate-800">{drill.group_name} · {drill.bucketLabel} · {total} invoice(s)</div>
          </div>
          <button data-testid="aging-drill-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0">
              <tr className="bg-slate-100 text-slate-600">
                {["Invoice No", "Date", "Client", "Project", "Net Payable", "Receipts", "CN", "DN",
                  "Ret. Pending", "Ret. Released", "Outstanding", "Net Collectible", "SES", "Due Date", "Basis", "Age", "Status"].map((h) => (
                  <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={17} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-2.5 py-1.5 font-semibold whitespace-nowrap">{r.invoice_number}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{(r.invoice_date || "").slice(0, 10)}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.party_client_name}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.project}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr(r.net_payable)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr(r.total_paid)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr(r.credit_note_total)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr(r.debit_note_total)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr(r.pending_retention)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr(r.total_retention_paid)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums font-bold">{inr(r.outstanding)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums text-sky-700 font-semibold">{inr(r.net_collectible)}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">
                    {r.ses_date
                      ? <span className="text-emerald-700 font-semibold">{(r.ses_date || "").slice(0, 10)}</span>
                      : <span className="text-amber-600 font-semibold">Pending</span>}
                  </td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.payment_due_date || <span className="text-rose-500 font-semibold">Missing</span>}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap text-slate-400">{(r.payment_due_source || "").replace(/_/g, " ")}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{r.age_days ?? "—"}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.receivable_status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2.5 border-t border-slate-100 flex items-center justify-between text-xs">
          <div className="text-slate-500">Page {page + 1} of {pages}</div>
          <div className="flex gap-1.5">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="px-2.5 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={13} /></button>
            <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}
              className="px-2.5 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronRight size={13} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
